/**
 * useCustomerIdentityApi — typed API client for the Customer Identity product.
 *
 * Wraps `useRegistryApi` calls against the `/identity/*` endpoints defined
 * in `application.registry.yml`. These endpoints are distinct from the
 * service-bindings API (invariant `separate-from-service-bindings`): the
 * Customer Identity product manages customer application OIDC clients,
 * user directories, and federation — not Rusha service bindings.
 *
 * Every method returns the registry hook's `{ call, loading, error }`
 * tuple so callers can drive per-action loading and error states.
 */
import { useCallback } from "react";

import { useRegistryApi } from "src/platform/hooks/useRegistryApi";

import type {
  CustomerIdentityActivation,
  CustomerIdentityStatus,
  IdentityClient,
  IdentityClientCredentials,
  IdentityProvider,
  IdentityProviderRemoveResponse,
  IdentityUser,
  IdentityUserDeleteResponse,
  IdentityUserListResponse,
  IdentityUserUpdateResponse,
} from "./customerIdentityTypes";

/**
 * Per-call options shared by all registry-driven calls.
 * `pathParams` carry URL parameters (e.g. `{ alias, clientId }`).
 */
interface CallOptions {
  pathParams?: Record<string, string>;
  body?: unknown;
}

/**
 * The shape returned by each method below. Mirrors the relevant subset of
 * `UseRegistryApiReturn` so consumers can read loading/error state.
 */
interface ApiMethod<T> {
  call: (options?: CallOptions) => Promise<T>;
  loading: boolean;
  error: string | null;
}

/**
 * Hook exposing typed methods for every Customer Identity endpoint.
 *
 * Usage:
 *   const identity = useCustomerIdentityApi();
 *   const status = await identity.getStatus({ pathParams: { alias } });
 */
export function useCustomerIdentityApi() {
  // ── Activation / status ───────────────────────────────────────────────
  const statusApi = useRegistryApi<CustomerIdentityStatus>(
    "application",
    "getCustomerIdentityStatus",
  );
  const activateApi = useRegistryApi<CustomerIdentityActivation>(
    "application",
    "activateCustomerIdentity",
  );
  const deactivateApi = useRegistryApi<CustomerIdentityActivation>(
    "application",
    "deactivateCustomerIdentity",
  );

  // ── Clients ───────────────────────────────────────────────────────────
  const listClientsApi = useRegistryApi<IdentityClient[]>(
    "application",
    "listIdentityClients",
  );
  const createClientApi = useRegistryApi<IdentityClient>(
    "application",
    "createIdentityClient",
  );
  const getClientApi = useRegistryApi<IdentityClient>(
    "application",
    "getIdentityClient",
  );
  const updateClientApi = useRegistryApi<IdentityClient>(
    "application",
    "updateIdentityClient",
  );
  const getCredentialsApi = useRegistryApi<IdentityClientCredentials>(
    "application",
    "getIdentityClientCredentials",
  );
  const rotateClientApi = useRegistryApi<IdentityClient>(
    "application",
    "rotateIdentityClient",
  );
  const revokeClientApi = useRegistryApi<IdentityClient>(
    "application",
    "revokeIdentityClient",
  );
  const cancelDeletionApi = useRegistryApi<IdentityClient>(
    "application",
    "cancelIdentityClientDeletion",
  );

  // ── Users ─────────────────────────────────────────────────────────────
  const listUsersApi = useRegistryApi<IdentityUserListResponse>(
    "application",
    "listIdentityUsers",
  );
  const createUserApi = useRegistryApi<IdentityUser>(
    "application",
    "createIdentityUser",
  );
  const updateUserApi = useRegistryApi<IdentityUserUpdateResponse>(
    "application",
    "updateIdentityUser",
  );
  const deleteUserApi = useRegistryApi<IdentityUserDeleteResponse>(
    "application",
    "deleteIdentityUser",
  );

  // ── Providers ─────────────────────────────────────────────────────────
  const listProvidersApi = useRegistryApi<IdentityProvider[]>(
    "application",
    "listIdentityProviders",
  );
  const addProviderApi = useRegistryApi<IdentityProvider>(
    "application",
    "addIdentityProvider",
  );
  const removeProviderApi = useRegistryApi<IdentityProviderRemoveResponse>(
    "application",
    "removeIdentityProvider",
  );

  // ── Bound wrappers (so callers pass only body/pathParams) ─────────────
  const getStatus = useCallback(
    (opts?: CallOptions) => statusApi.call({ pathParams: opts?.pathParams }),
    [statusApi],
  );
  const activate = useCallback(
    (opts?: CallOptions) => activateApi.call({ pathParams: opts?.pathParams }),
    [activateApi],
  );
  const deactivate = useCallback(
    (opts?: CallOptions) =>
      deactivateApi.call({ pathParams: opts?.pathParams }),
    [deactivateApi],
  );

  const listClients = useCallback(
    (opts?: CallOptions) =>
      listClientsApi.call({ pathParams: opts?.pathParams }),
    [listClientsApi],
  );
  const createClient = useCallback(
    (opts?: CallOptions) =>
      createClientApi.call({ pathParams: opts?.pathParams, body: opts?.body }),
    [createClientApi],
  );
  const getClient = useCallback(
    (opts?: CallOptions) => getClientApi.call({ pathParams: opts?.pathParams }),
    [getClientApi],
  );
  const getClientCredentials = useCallback(
    (opts?: CallOptions) =>
      getCredentialsApi.call({ pathParams: opts?.pathParams }),
    [getCredentialsApi],
  );
  const updateClient = useCallback(
    (opts?: CallOptions) =>
      updateClientApi.call({ pathParams: opts?.pathParams, body: opts?.body }),
    [updateClientApi],
  );
  const rotateClient = useCallback(
    (opts?: CallOptions) =>
      rotateClientApi.call({ pathParams: opts?.pathParams }),
    [rotateClientApi],
  );
  const revokeClient = useCallback(
    (opts?: CallOptions) =>
      revokeClientApi.call({ pathParams: opts?.pathParams }),
    [revokeClientApi],
  );
  const cancelDeletion = useCallback(
    (opts?: CallOptions) =>
      cancelDeletionApi.call({ pathParams: opts?.pathParams }),
    [cancelDeletionApi],
  );

  const listUsers = useCallback(
    (opts?: CallOptions) => listUsersApi.call({ pathParams: opts?.pathParams }),
    [listUsersApi],
  );
  const createUser = useCallback(
    (opts?: CallOptions) =>
      createUserApi.call({ pathParams: opts?.pathParams, body: opts?.body }),
    [createUserApi],
  );
  const updateUser = useCallback(
    (opts?: CallOptions) =>
      updateUserApi.call({ pathParams: opts?.pathParams, body: opts?.body }),
    [updateUserApi],
  );
  const deleteUser = useCallback(
    (opts?: CallOptions) =>
      deleteUserApi.call({ pathParams: opts?.pathParams }),
    [deleteUserApi],
  );

  const listProviders = useCallback(
    (opts?: CallOptions) =>
      listProvidersApi.call({ pathParams: opts?.pathParams }),
    [listProvidersApi],
  );
  const addProvider = useCallback(
    (opts?: CallOptions) =>
      addProviderApi.call({ pathParams: opts?.pathParams, body: opts?.body }),
    [addProviderApi],
  );
  const removeProvider = useCallback(
    (opts?: CallOptions) =>
      removeProviderApi.call({ pathParams: opts?.pathParams }),
    [removeProviderApi],
  );

  return {
    // activation
    getStatus: wrap(statusApi, getStatus),
    activate: wrap(activateApi, activate),
    deactivate: wrap(deactivateApi, deactivate),
    // clients
    listClients: wrap(listClientsApi, listClients),
    createClient: wrap(createClientApi, createClient),
    getClient: wrap(getClientApi, getClient),
    updateClient: wrap(updateClientApi, updateClient),
    getClientCredentials: wrap(getCredentialsApi, getClientCredentials),
    rotateClient: wrap(rotateClientApi, rotateClient),
    revokeClient: wrap(revokeClientApi, revokeClient),
    cancelDeletion: wrap(cancelDeletionApi, cancelDeletion),
    // users
    listUsers: wrap(listUsersApi, listUsers),
    createUser: wrap(createUserApi, createUser),
    updateUser: wrap(updateUserApi, updateUser),
    deleteUser: wrap(deleteUserApi, deleteUser),
    // providers
    listProviders: wrap(listProvidersApi, listProviders),
    addProvider: wrap(addProviderApi, addProvider),
    removeProvider: wrap(removeProviderApi, removeProvider),
  };
}

/**
 * Compose a method that exposes `call`, `loading`, and `error` from the
 * underlying registry hook while delegating execution to the bound wrapper.
 */
function wrap<T>(
  api: { loading: boolean; error: string | null },
  fn: (opts?: CallOptions) => Promise<T>,
): ApiMethod<T> {
  return {
    call: fn,
    loading: api.loading,
    error: api.error,
  };
}
